import React, { useState, useEffect, useCallback } from 'react';
import { getNoSaleReasonCodes } from '../services';
import { NoSaleReasonCodeOption } from '../types';
import { i18n } from '../../../../translate/i18n';

type SaleOutcome = 'completed' | 'not_completed' | null;

interface ReturnToQueueModalProps {
  isOpen: boolean;
  sellerName: string;
  onConfirm: (data: ReturnToQueueData) => void;
  onCancel: () => void;
  title?: string;
  introText?: string;
  passwordIntroText?: string;
  confirmText?: string;
}

export interface ReturnToQueueData {
  password: string;
  saleOutcome: SaleOutcome;
  noSaleReasonId?: number;
}

const ReturnToQueueModal: React.FC<ReturnToQueueModalProps> = ({
  isOpen,
  sellerName,
  onConfirm,
  onCancel,
  title = 'Retornar à Fila',
  introText = 'Vendedor:',
  passwordIntroText = 'Para confirmar o retorno à fila, informe a senha do vendedor, do gerente ou do administrador:',
  confirmText = 'Confirmar Retorno',
}) => {
  const tModal = (key: string, fallback: string) => {
    const translated = String(i18n.t(key));
    return translated !== key ? translated : fallback;
  };

  const [step, setStep] = useState<'questionnaire' | 'password'>('questionnaire');
  const [saleOutcome, setSaleOutcome] = useState<SaleOutcome>(null);
  const [noSaleReasonId, setNoSaleReasonId] = useState<number | undefined>(undefined);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [reasonOptions, setReasonOptions] = useState<NoSaleReasonCodeOption[]>([]);
  const [loadingReasons, setLoadingReasons] = useState(false);
  const [reasonsError, setReasonsError] = useState<string | null>(null);

  const loadReasonOptions = useCallback(async () => {
    setLoadingReasons(true);
    setReasonsError(null);

    try {
      const codes = await getNoSaleReasonCodes();
      setReasonOptions(codes);
    } catch {
      const errorKey = 'seller_queue.modal.reasons.load_error';
      const translated = String(i18n.t(errorKey));
      setReasonsError(
        translated !== errorKey
          ? translated
          : 'Não foi possível carregar os motivos. Tente novamente.'
      );
    } finally {
      setLoadingReasons(false);
    }
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setStep('questionnaire');
      setSaleOutcome(null);
      setNoSaleReasonId(undefined);
      setPassword('');
      setShowPassword(false);
      setReasonsError(null);
      return;
    }

    if (reasonOptions.length === 0) {
      void loadReasonOptions();
    }
  }, [isOpen, reasonOptions.length, loadReasonOptions]);

  useEffect(() => {
    if (saleOutcome === 'not_completed' && reasonOptions.length === 0) {
      void loadReasonOptions();
    }
  }, [saleOutcome, reasonOptions.length, loadReasonOptions]);

  const canProceedToPassword = () => {
    if (!saleOutcome) return false;
    if (saleOutcome === 'not_completed' && !noSaleReasonId) return false;
    return true;
  };

  const handleProceedToPassword = () => {
    if (canProceedToPassword()) {
      setStep('password');
    }
  };

  const handleConfirm = () => {
    if (!password.trim() || !saleOutcome) return;
    onConfirm({ password, saleOutcome, noSaleReasonId });
    setPassword('');
    setShowPassword(false);
  };

  const handleCancel = () => {
    setPassword('');
    setShowPassword(false);
    setSaleOutcome(null);
    setNoSaleReasonId(undefined);
    setStep('questionnaire');
    onCancel();
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: 'rgba(0,0,0,0.5)',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: 8,
          padding: 32,
          width: 420,
          boxShadow: '0 4px 24px rgba(0,0,0,0.18)',
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: 4, fontSize: 18 }}>
          {title}
        </h3>
        <p style={{ color: '#666', marginBottom: 20, fontSize: 14 }}>
          {introText} <strong>{sellerName}</strong>
        </p>

        {step === 'questionnaire' && (
          <>
            <p style={{ fontWeight: 600, marginBottom: 12 }}>Resultado do atendimento:</p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  border: `2px solid ${saleOutcome === 'completed' ? '#28a745' : '#ddd'}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: saleOutcome === 'completed' ? '#f0fff4' : '#fafafa',
                }}
              >
                <input
                  type="radio"
                  name="saleOutcome"
                  value="completed"
                  checked={saleOutcome === 'completed'}
                  onChange={() => {
                    setSaleOutcome('completed');
                    setNoSaleReasonId(undefined);
                  }}
                />
                <span>{tModal('seller_queue.modal.sale_outcome.completed', 'Venda Concluída')}</span>
              </label>

              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '10px 14px',
                  border: `2px solid ${saleOutcome === 'not_completed' ? '#dc3545' : '#ddd'}`,
                  borderRadius: 6,
                  cursor: 'pointer',
                  background: saleOutcome === 'not_completed' ? '#fff5f5' : '#fafafa',
                }}
              >
                <input
                  type="radio"
                  name="saleOutcome"
                  value="not_completed"
                  checked={saleOutcome === 'not_completed'}
                  onChange={() => setSaleOutcome('not_completed')}
                />
                <span>{tModal('seller_queue.modal.sale_outcome.not_completed', 'Venda Não Concluída')}</span>
              </label>
            </div>

            {saleOutcome === 'not_completed' && (
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontWeight: 600, marginBottom: 8 }}>
                  {tModal('seller_queue.modal.reasons.label', 'Motivo da Não Venda:')}
                </label>
                {loadingReasons ? (
                  <p style={{ color: '#888' }}>{tModal('seller_queue.modal.reasons.loading', 'Carregando motivos...')}</p>
                ) : reasonsError ? (
                  <div>
                    <p style={{ color: '#dc3545', marginBottom: 8 }}>{reasonsError}</p>
                    <button
                      type="button"
                      onClick={() => void loadReasonOptions()}
                      style={{
                        padding: '6px 12px',
                        borderRadius: 4,
                        border: '1px solid #ccc',
                        background: '#f5f5f5',
                        cursor: 'pointer',
                        fontSize: 13,
                      }}
                    >
                      {tModal('seller_queue.modal.reasons.retry', 'Tentar novamente')}
                    </button>
                  </div>
                ) : (
                  <select
                    value={noSaleReasonId ?? ''}
                    onChange={(e) =>
                      setNoSaleReasonId(e.target.value ? Number(e.target.value) : undefined)
                    }
                    style={{
                      width: '100%',
                      padding: '9px 12px',
                      border: '1px solid #ccc',
                      borderRadius: 4,
                      fontSize: 14,
                    }}
                  >
                    <option value="">{tModal('seller_queue.modal.reasons.placeholder', 'Selecione um motivo...')}</option>
                    {reasonOptions.map((opt) => (
                      <option key={opt.id} value={opt.id}>
                        {opt.saleReasonCode}
                      </option>
                    ))}
                  </select>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                onClick={handleCancel}
                style={{
                  padding: '8px 20px',
                  borderRadius: 4,
                  border: '1px solid #ccc',
                  background: '#f5f5f5',
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                {tModal('seller_queue.modal.buttons.cancel', 'Cancelar')}
              </button>
              <button
                onClick={handleProceedToPassword}
                disabled={!canProceedToPassword()}
                style={{
                  padding: '8px 20px',
                  borderRadius: 4,
                  border: 'none',
                  background: canProceedToPassword() ? '#007bff' : '#b0c4de',
                  color: '#fff',
                  cursor: canProceedToPassword() ? 'pointer' : 'not-allowed',
                  fontSize: 14,
                }}
              >
                {tModal('seller_queue.modal.buttons.next', 'Próximo →')}
              </button>
            </div>
          </>
        )}

        {step === 'password' && (
          <>
            <p style={{ color: '#555', marginBottom: 16 }}>
              {passwordIntroText}
            </p>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleConfirm();
              }}
            >
              <div style={{ position: 'relative', marginBottom: 20 }}>
                <input
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha"
                  autoFocus
                  style={{
                    width: '100%',
                    padding: '10px 12px',
                    paddingRight: 40,
                    border: '1px solid #ccc',
                    borderRadius: 4,
                    fontSize: 15,
                    boxSizing: 'border-box',
                  }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  style={{
                    position: 'absolute',
                    right: 12,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 16,
                    color: '#666',
                    padding: 4,
                  }}
                  title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
                >
                  <i className={`fas fa-eye${showPassword ? '' : '-slash'}`}></i>
                </button>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
                <button
                  type="button"
                  onClick={() => {
                    setShowPassword(false);
                    setStep('questionnaire');
                  }}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 4,
                    border: '1px solid #ccc',
                    background: '#f5f5f5',
                    cursor: 'pointer',
                    fontSize: 14,
                  }}
                >
                  ← Voltar
                </button>
                <button
                  type="submit"
                  disabled={!password.trim()}
                  style={{
                    padding: '8px 20px',
                    borderRadius: 4,
                    border: 'none',
                    background: password.trim() ? '#28a745' : '#b0c4de',
                    color: '#fff',
                    cursor: password.trim() ? 'pointer' : 'not-allowed',
                    fontSize: 14,
                  }}
                >
                  {confirmText}
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
};

export default ReturnToQueueModal;
